/**
 * SOUND-643 Phase 2 — per-field inline editing on the registration record.
 *
 * The Salesforce pattern: a pencil per row, one field at a time, no wizard. The
 * draft wizard carries a "Submit for review" button that on an approved
 * application would regress the status, overwrite `submittedAt` and re-notify
 * the merchant — so the correction path deliberately has no submit anywhere
 * near it.
 *
 * Rules live here with tests because the component that draws them cannot be
 * tested in this repo.
 */

/**
 * Sections whose value is an ARRAY. sound-api treats these as ARRAY_FIELDS and
 * WHOLESALE-REPLACES them rather than merging, so a payload carrying one field
 * of one row would delete every other row.
 */
export const ARRAY_BACKED_SECTIONS = ["owners", "dbas", "documents"];

/**
 * Array-backed sections that DO have a per-row endpoint, so a single row can be
 * edited without touching its siblings.
 *
 * `owners` joined this list in SOUND-649: owners became their own collection and
 * gained PUT /applications/:id/owners/:ownerId. Before that they were read-only
 * here — not an oversight, a refusal to ship a surface that silently dropped
 * owners, which is the bug Media Resellers reported (SOUND-647).
 *
 * `dbas` also has an endpoint but keeps its own dedicated editor tile, so it is
 * deliberately absent — two editors for one section would disagree eventually.
 * `documents` has none and is genuinely read-only here.
 */
export const ROW_EDITABLE_SECTIONS = ["owners"];

export function isRowEditableSection(sectionKey) {
  return ROW_EDITABLE_SECTIONS.includes(sectionKey);
}

export function isArrayBackedSection(sectionKey) {
  return ARRAY_BACKED_SECTIONS.includes(sectionKey);
}

/**
 * Statuses in which a non-internal caller may still edit their own registration.
 * Mirrors REGISTRATION_CLIENT_EDITABLE_STATUSES in sound-api
 * config/statusMachines.mjs — `needs_info` is included because the merchant
 * wizard stays on that status so the merchant can supply what was asked for.
 */
export const CLIENT_EDITABLE_STATUSES = ["new", "in_progress", "needs_info"];

/**
 * May this user inline-edit fields in this section?
 *
 * Internal at any status is the whole point of SOUND-636 — ops correcting a
 * wrong bank account after approval without a Mongo write. The API re-checks via
 * canEditRegistration, so drift shows up as a control that 403s rather than as
 * silent data loss.
 *
 * @param {{ sectionKey?: string, isInternal?: boolean, isPartner?: boolean, status?: string }} args
 */
export function canEditSectionField({
  sectionKey,
  isInternal = false,
  isPartner = false,
  status,
} = {}) {
  // Array-backed WITHOUT a per-row endpoint stays read-only — a save would
  // replace the whole array and delete the rows it did not carry.
  if (isArrayBackedSection(sectionKey) && !isRowEditableSection(sectionKey)) {
    return false;
  }
  if (isInternal) return true;
  if (isPartner) return CLIENT_EDITABLE_STATUSES.includes(status);
  return false;
}

/**
 * Build the PUT body for a single-field save.
 *
 * Returns only that field, nested under its section. sound-api deep-merges
 * scalar keys, so siblings are untouched and no new endpoint is needed — which
 * is why this works at all without backend work.
 *
 * Values are sent as the input produced them, with no type coercion: the
 * registration wizard already writes strings for number fields, and the API
 * validator accepts either (`isNaN(Number(value))`). Coercing here would make
 * inline edits write a different type than the wizard writes to the same field.
 *
 * @throws if asked to build a payload for an array-backed section
 */
export function buildFieldPayload(sectionKey, fieldName, value) {
  if (isRowEditableSection(sectionKey)) {
    throw new Error(
      `buildFieldPayload: "${sectionKey}" is row-addressable. Use buildRowFieldPayload with the row's id.`
    );
  }
  if (isArrayBackedSection(sectionKey)) {
    throw new Error(
      `buildFieldPayload: "${sectionKey}" is array-backed and would be wholesale-replaced by the API. Use a per-row endpoint.`
    );
  }
  if (!sectionKey || !fieldName) {
    throw new Error("buildFieldPayload: sectionKey and fieldName are required");
  }
  return { [sectionKey]: { [fieldName]: value } };
}

/**
 * Has the value actually changed? Treats null/undefined/"" as equivalent, so
 * opening and closing an untouched empty field is never a save — the API
 * returns `undefined` for an unset field and a controlled input returns "".
 */
export function isFieldDirty(original, next) {
  const norm = (v) => (v === null || v === undefined || v === "" ? "" : v);
  return String(norm(original)) !== String(norm(next));
}

/**
 * Build the PUT body for a single field on a single ROW.
 *
 * Flat, not nested under a section: the per-row endpoint already knows which
 * owner it is addressing from the URL, and it applies a partial $set — so
 * fields the payload omits are left alone rather than blanked. That is the
 * whole difference from the array path, where an omitted sibling was deleted.
 */
export function buildRowFieldPayload(fieldName, value) {
  if (!fieldName) throw new Error("buildRowFieldPayload: fieldName is required");
  return { [fieldName]: value };
}

/**
 * The message for a refused single-field save, and whether it is about THIS
 * field — so the inline editor can show it under the input instead of only in
 * a toast that disappears.
 *
 * Shapes sound-api returns:
 *   400 { error, details: [{ field, message }] }   validation (e.g. an owner
 *                                                   title off the Stratus list)
 *   403 { error, fields: ["title"] }               a non-internal caller writing
 *                                                   an internal-only field
 *   4xx/5xx { error }
 *
 * `details` entries may also be bare strings (older validators).
 *
 * @returns {{ message: string, forField: boolean }}
 */
export function describeSaveError(body, fieldName) {
  const details = Array.isArray(body?.details) ? body.details : null;
  if (details && details.length) {
    const own = details.find((d) => d && typeof d === "object" && d.field === fieldName);
    if (own) return { message: own.message || body.error || "Invalid value", forField: true };
    const text = details
      .slice(0, 3)
      .map((d) => (typeof d === "string" ? d : d?.message))
      .filter(Boolean)
      .join("; ");
    return { message: text || body.error || "Invalid value", forField: false };
  }
  const fields = Array.isArray(body?.fields) ? body.fields : [];
  return {
    message: body?.error || "Unknown error",
    forField: fieldName ? fields.includes(fieldName) : false,
  };
}
